import { useEffect, useRef, useState } from "react";
import Spinner from "../../components/spinner/spinner";
import { Toast } from "primereact/toast";
import {
  getCurrencyConversionData,
  getLatestCurrencyConversion,
  postManualCurrencyRates
} from "../../services/currencyConversionService";
import * as constants from "../../constants/constants";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { currencyConversionColumns } from "../../constants/table-columns/tableColumns";
import { Form, Formik } from "formik";
import { Calendar } from "primereact/calendar";
import styles from "./currencyConversion.module.scss";
import { Button } from "primereact/button";
import { format, parseISO, set } from "date-fns";
import { Dialog } from "primereact/dialog";
import { InputNumber } from "primereact/inputnumber";
import { Tag } from "primereact/tag";

const initialValue = {
  startDate: null,
  endDate: null
};

const addManualCurrencyConversionRatesInitialValue = {
  usdToMxn: null,
  usdToCad: null,
  mxnToUsd: null,
  cadToUsd: null
};

const CurrencyConversion = () => {
  const toast = useRef(null);
  const [showSpinner, setShowSpinner] = useState(false);
  const formRef = useRef(null);
  const addManualCurrencyConversionRatesFormRef = useRef(null);
  const [disableFields, setDisableFields] = useState(false);
  const [currencyConversionData, setCurrencyConversionData] = useState([]);
  const [addManuallyDialogVisible, setAddManuallyDialogVisible] =
    useState(false);

  function extractDate(dateTimeString) {
    if (!dateTimeString || isNaN(Date.parse(dateTimeString))) {
      return null;
    }
    return dateTimeString.split("T")[0];
  }

  function extractTime(dateTimeString) {
    if (!dateTimeString || isNaN(Date.parse(dateTimeString))) {
      return null;
    }

    let timePart = dateTimeString.split("T")[1];
    let hours = parseInt(timePart.split(":")[0], 10);
    let minutes = timePart.split(":")[1];

    const ampm = hours >= 12 ? "PM" : "AM";
    hours = hours % 12;
    hours = hours ? hours : 12;
    return `${hours}:${minutes} ${ampm}`;
  }

  function formatDate(date) {
    var d = new Date(date),
      month = "" + (d.getMonth() + 1),
      day = "" + d.getDate(),
      year = d.getFullYear();

    if (month.length < 2) month = "0" + month;
    if (day.length < 2) day = "0" + day;

    return [year, month, day].join("-");
  }

  useEffect(() => {
    fetchCurrencyConversionData();
  }, []);

  const fetchCurrencyConversionData = async (formBody) => {
    try {
      setShowSpinner(true);

      formBody = {
        startDate: formBody?.startDate ? formatDate(formBody?.startDate) : null,
        endDate: formBody?.endDate ? formatDate(formBody?.endDate) : null
      };

      let result = await getCurrencyConversionData(formBody);

      setCurrencyConversionData(result);

      setShowSpinner(false);
    } catch (error) {
      setShowSpinner(false);
      setCurrencyConversionData([]);

      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: error.message,
        life: 4000
      });
    }
  };

  const dateColumnFormat = (rowData) => {
    return extractDate(rowData.dateTime);
  };

  const timeColumnFormat = (rowData) => {
    return extractTime(rowData.dateTime);
  };

  const fetchLatestCurrencyConversion = async () => {
    try {
      setShowSpinner(true);

      let result = await getLatestCurrencyConversion();

      if (result.statusCode === 200) {
        fetchCurrencyConversionData();
      }

      setShowSpinner(false);
    } catch (error) {
      setShowSpinner(false);
      setCurrencyConversionData([]);

      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: error.message,
        life: 4000
      });
    }
  };

  const onHideAddManuallyDialog = () => {
    setAddManuallyDialogVisible(false);
  };

  const showAddManuallyDialog = () => {
    setAddManuallyDialogVisible(true);
  };

  const postManualUserCurrencyRates = async (payload) => {
    try {
      setShowSpinner(true);

      let result = await postManualCurrencyRates(payload);

      if (result.statusCode === 201) {
        setAddManuallyDialogVisible(false);
        fetchCurrencyConversionData();
      }

      setShowSpinner(false);
    } catch (error) {
      setShowSpinner(false);

      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: error.message,
        life: 4000
      });
    }
  };

  const updatedByColumnFormat = (data) => {
    let severityValue = "";
    let UpdatedByFormat = "";

    switch (data.updatedBy) {
      case "System":
        severityValue = "info";
        UpdatedByFormat = "System";
        break;
      case "UserFetched":
        severityValue = "success";
        UpdatedByFormat = "User Fetched";
        break;
      case "UserCreated":
        severityValue = "";
        UpdatedByFormat = "User Created";
        break;
      default:
        severityValue = "";
        UpdatedByFormat = data.updatedBy;
    }

    return (
      <div>
        <Tag
          severity={severityValue}
          value={UpdatedByFormat}
          rounded
          style={{
            marginLeft: "1rem",
            marginTop: "0.2rem",
            marginBottom: "0.2rem"
          }}
        ></Tag>
      </div>
    );
  };

  const usdToMxnColumnFormat = (rowData) => {
    return (
      <>
        <span style={{ fontWeight: "bold" }}>MX$ </span>
        {rowData.usdToMxn}
      </>
    );
  };

  const usdToCadColumnFormat = (rowData) => {
    return (
      <>
        <span style={{ fontWeight: "bold" }}>CA$ </span>
        {rowData.usdToCad}
      </>
    );
  };

  const mxnToUsdColumnFormat = (rowData) => {
    return (
      <>
        <span style={{ fontWeight: "bold" }}>$ </span>
        {rowData.mxnToUsd}
      </>
    );
  };

  const cadToUsdColumnFormat = (rowData) => {
    return (
      <>
        <span style={{ fontWeight: "bold" }}>$ </span>
        {rowData.cadToUsd}
      </>
    );
  };

  return (
    <>
      {showSpinner && <Spinner />}

      <Dialog
        visible={addManuallyDialogVisible}
        modal
        header="Add Manual Currency Conversion Rates"
        style={{ width: "50vw" }}
        breakpoints={{ "1024px": "75vw", "641px": "100vw" }}
        onHide={onHideAddManuallyDialog}
        maximizable
      >
        <Formik
          initialValues={addManualCurrencyConversionRatesInitialValue}
          onSubmit={(values) => {
            setShowSpinner(true);
            postManualUserCurrencyRates(values);
          }}
          innerRef={addManualCurrencyConversionRatesFormRef}
        >
          {({ values, handleChange, handleSubmit, setFieldValue }) => (
            <Form
              onSubmit={handleSubmit}
              ref={addManualCurrencyConversionRatesFormRef}
            >
              <div className="flex flex-wrap justify-content-center gap-4">
                <div className="flex flex-column gap-2">
                  <label htmlFor="usdToMxn" className={styles.formFieldLabel}>
                    USD to MXN
                  </label>
                  <InputNumber
                    id="usdToMxn"
                    value={values?.usdToMxn}
                    onValueChange={handleChange}
                    showButtons
                    buttonLayout="horizontal"
                    step={1.0}
                    decrementButtonClassName={styles.decrementBtn}
                    incrementButtonClassName={styles.incrementBtn}
                    incrementButtonIcon="pi pi-plus"
                    decrementButtonIcon="pi pi-minus"
                    mode="currency"
                    currency="MXN"
                    placeholder="0.00"
                  />
                </div>

                <div className="flex flex-column gap-2">
                  <label htmlFor="usdToCad" className={styles.formFieldLabel}>
                    USD to CAD
                  </label>
                  <InputNumber
                    id="usdToCad"
                    value={values?.usdToCad}
                    onValueChange={handleChange}
                    showButtons
                    buttonLayout="horizontal"
                    step={1.0}
                    decrementButtonClassName={styles.decrementBtn}
                    incrementButtonClassName={styles.incrementBtn}
                    incrementButtonIcon="pi pi-plus"
                    decrementButtonIcon="pi pi-minus"
                    mode="currency"
                    currency="CAD"
                    placeholder="0.00"
                  />
                </div>

                <div className="flex flex-column gap-2">
                  <label htmlFor="mxnToUsd" className={styles.formFieldLabel}>
                    MXN to USD
                  </label>
                  <InputNumber
                    id="mxnToUsd"
                    value={values?.mxnToUsd}
                    onValueChange={handleChange}
                    showButtons
                    buttonLayout="horizontal"
                    step={0.1}
                    decrementButtonClassName={styles.decrementBtn}
                    incrementButtonClassName={styles.incrementBtn}
                    incrementButtonIcon="pi pi-plus"
                    decrementButtonIcon="pi pi-minus"
                    mode="currency"
                    currency="USD"
                    placeholder="0.00"
                  />
                </div>

                <div className="flex flex-column gap-2">
                  <label htmlFor="cadToUsd" className={styles.formFieldLabel}>
                    CAD to USD
                  </label>
                  <InputNumber
                    id="cadToUsd"
                    value={values?.cadToUsd}
                    onValueChange={handleChange}
                    showButtons
                    buttonLayout="horizontal"
                    step={0.1}
                    decrementButtonClassName={styles.decrementBtn}
                    incrementButtonClassName={styles.incrementBtn}
                    incrementButtonIcon="pi pi-plus"
                    decrementButtonIcon="pi pi-minus"
                    mode="currency"
                    currency="USD"
                    placeholder="0.00"
                  />
                </div>
              </div>

              <div className="flex justify-content-end gap-2 mt-6">
                <Button
                  label="Send"
                  type="submit"
                  size="medium"
                  className={styles.fetchLatestBtn}
                ></Button>

                <Button
                  label="Reset"
                  type="button"
                  size="medium"
                  onClick={(e) => {
                    addManualCurrencyConversionRatesFormRef.current.setValues({
                      usdToMxn: null,
                      usdToCad: null,
                      mxnToUsd: null,
                      cadToUsd: null
                    });
                  }}
                  className={styles.resetBtn}
                />
              </div>
            </Form>
          )}
        </Formik>
      </Dialog>

      <br></br>

      <div className="flex justify-content-between">
        <div className="flex align-items-center justify-content-center">
          <Formik
            initialValues={initialValue}
            onSubmit={(values) => {
              setShowSpinner(true);
              fetchCurrencyConversionData(values);
            }}
            innerRef={formRef}
          >
            {({ values, handleChange, handleSubmit, setFieldValue }) => (
              <Form onSubmit={handleSubmit} ref={formRef}>
                <div className="flex gap-2">
                  <div className="flex align-items-center justify-content-center">
                    <div className={styles.formLabelContainer}></div>
                    <Calendar
                      id="startDate"
                      value={values?.startDate}
                      onChange={handleChange}
                      disabled={disableFields}
                      placeholder="Start Date"
                      maxDate={formRef?.current?.values?.endDate}
                    />
                  </div>
                  <div className="flex align-items-center justify-content-center">
                    <div className={styles.formLabelContainer}></div>
                    <Calendar
                      id="endDate"
                      value={values?.endDate}
                      onChange={handleChange}
                      disabled={disableFields}
                      placeholder="End Date"
                      minDate={formRef?.current?.values?.startDate}
                    />
                  </div>

                  <div className="flex align-items-center justify-content-center gap-2">
                    <div className="flex align-items-center justify-content-center">
                      <div className={styles.formLabelContainer}></div>
                      <Button
                        label="Search"
                        type="submit"
                        size="medium"
                        className={styles.primaryBtn}
                      />
                    </div>
                    <div className="flex align-items-center justify-content-center">
                      <div className={styles.formLabelContainer}></div>
                      <Button
                        label="Reset"
                        type="button"
                        size="medium"
                        onClick={(e) => {
                          formRef.current.setValues({
                            startDate: null,
                            endDate: null
                          });
                          fetchCurrencyConversionData({
                            startDate: null,
                            endDate: null
                          });
                          setDisableFields(false);
                        }}
                        className={styles.resetBtn}
                      />
                    </div>
                  </div>
                </div>
              </Form>
            )}
          </Formik>
        </div>

        <div className="flex align-items-center justify-content-center gap-3">
          <div className="flex align-items-center justify-content-center">
            <Button
              label="Add Manual Rates"
              type="button"
              size="medium"
              onClick={showAddManuallyDialog}
            ></Button>
          </div>
          <div className="flex align-items-center justify-content-center">
            <Button
              label="Fetch Current Rates"
              type="button"
              size="medium"
              className={styles.fetchLatestBtn}
              onClick={fetchLatestCurrencyConversion}
            ></Button>
          </div>
        </div>
      </div>

      <br></br>

      <DataTable
        value={currencyConversionData}
        tableStyle={{ minWidth: "50rem" }}
        scrollable
        paginator={constants.paginator}
        rows={constants.defaultRows}
        rowsPerPageOptions={constants.rowsPerPageOptions}
        paginatorTemplate={constants.paginatorTemplate}
        currentPageReportTemplate={constants.currentPageReportTemplate}
        emptyMessage={constants.emptyMessage}
        dataKey="id"
        scrollHeight="30vw"
      >
        {currencyConversionColumns.map((col, index) => (
          <Column
            key={index}
            field={col.field}
            header={col.header}
            sortable={col.sort}
            style={{ minWidth: col.minWidth }}
            body={
              col.field.toLocaleLowerCase() === "date"
                ? dateColumnFormat
                : col.field.toLocaleLowerCase() === "time"
                ? timeColumnFormat
                : col.field.toLocaleLowerCase() === "updatedby"
                ? updatedByColumnFormat
                : col.field.toLocaleLowerCase() === "usdtomxn"
                ? usdToMxnColumnFormat
                : col.field.toLocaleLowerCase() === "usdtocad"
                ? usdToCadColumnFormat
                : col.field.toLocaleLowerCase() === "mxntousd"
                ? mxnToUsdColumnFormat
                : col.field.toLocaleLowerCase() === "cadtousd"
                ? cadToUsdColumnFormat
                : col.value
            }
          />
        ))}
      </DataTable>

      <Toast ref={toast} position="center" />
    </>
  );
};

export default CurrencyConversion;
